import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { createReactRenderer } from 'helpers/react-renderer';
import Autocomplete from 'pages/components/Autocomplete';

import Card, { CardSection } from '@lightspeed/cirrus-card';
import Text from '@lightspeed/cirrus-text';
import { Badge } from '@lightspeed/flame/Badge';
import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { Divider } from '@lightspeed/flame/Divider';

import map from 'lodash/map';
import get from 'lodash/get';
import find from 'lodash/find';

import Format from 'helpers/format';

const triggerViewChange = () => window.viewFieldChanged('view');

export default class Transfer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      status: '',
      employee: '',
      sentDate: '',
      shops: [],
      fromShop: '',
      toShop: '',
    };

    this.dateRef = React.createRef();
  }

  componentDidMount() {
    this.setTransferData();
    this.setFromAndToShops();
    this.appendNeedByDateFields();
  }

  // eslint-disable-next-line react/sort-comp
  handleShopChange({ value, selected } = {}) {
    const { shops, fromShop, toShop } = this.state;
    let fromShopValue = fromShop;
    let toShopValue = toShop;

    if (selected === 'from') {
      fromShopValue = value;
      toShopValue = this.getOppositeShopValue(fromShopValue, toShop, shops);
    } else if (selected === 'to') {
      toShopValue = value;
      fromShopValue = this.getOppositeShopValue(toShopValue, fromShop, shops);
    }

    this.setState({
      fromShop: fromShopValue,
      toShop: toShopValue,
    });

    this.setGUIDefField('from', fromShopValue);
    this.setGUIDefField('to', toShopValue);

    triggerViewChange();
  }

  getShops() {
    const { shops } = this.props;
    return map(shops, shop => ({
      value: shop.shopId,
      label: shop.name,
    }));
  }

  getOppositeShopValue(selectedShopValue, oppositeShopValue, shops) {
    /**
     * When selection is empty, null do not change the opposite shop
     */
    if (!selectedShopValue) {
      return oppositeShopValue;
    }
    /**
     * When there are only two shops available select the opposite shop
     */
    if (shops.length === 2) {
      const oppositeShop = find(shops, shop => shop.value !== selectedShopValue);
      if (oppositeShop) {
        return oppositeShop.value;
      }
    }
    /**
     * When there are more than two shops available
     * and the shops are the same, clear the opposite shop
     */
    if (selectedShopValue === oppositeShopValue) {
      return '';
    }

    return oppositeShopValue;
  }

  setFromAndToShops() {
    const { transfer, currentShopId } = this.props;
    const shops = this.getShops();
    const fromShopValue = get(transfer, 'fromShopId', currentShopId);
    let toShopValue = get(transfer, 'toShopId', '');

    if (!toShopValue) {
      toShopValue = this.getOppositeShopValue(fromShopValue, toShopValue, shops);
      this.setGUIDefField('to', toShopValue);

      if (toShopValue) {
        triggerViewChange();
      }
    }

    this.setState({
      shops,
      fromShop: fromShopValue,
      toShop: toShopValue,
    });
  }

  setTransferData() {
    const { transfer } = this.props;
    const sentDate = get(transfer, 'sentDate.date');
    let sentDateFormatted;

    if (transfer && sentDate) {
      sentDateFormatted = Format.formatDateTime(sentDate, transfer.dateTimeFormat, true);
    }
    this.setState({
      status: get(transfer, 'status', 'open'),
      employee: get(transfer, 'createdBy'),
      sentDate: sentDateFormatted,
    });
  }

  setGUIDefField(id, value) {
    const { inputs } = this.props;
    const el = document.getElementById(get(inputs, id));
    el.value = value;
  }

  appendNeedByDateFields() {
    const { inputs } = this.props;
    const children = [...document.getElementById(get(inputs, 'needBy')).parentElement.childNodes];
    children.forEach(el => el && this.dateRef.current.appendChild(el));
  }

  render() {
    const { transfer, canChangeShops } = this.props;
    const { status, employee, sentDate, shops, fromShop, toShop } = this.state;
    const transferLabel = transfer === null ? 'New Transfer' : `Transfer #${transfer.transferId}`;
    return (
      <div className="transfer-view">
        <div className="transfer-view__summary">
          <Card>
            <CardSection>
              <Text heading3 className="transfer-view__label">
                {transferLabel}
              </Text>
            </CardSection>
            <Divider />
            <CardSection>
              <div className="transfer-view__status">
                <div className="cr-mb-1">
                  <Text bodySmall dimmed>
                    Status
                  </Text>
                </div>
                {(status === 'open' || status === 'reopened') && <Badge bg="gray-100">open</Badge>}
                {status === 'sent' && (
                  <Badge type="warning" color="orange-200">
                    sent
                  </Badge>
                )}
                {status === 'received' && (
                  <Badge type="info" bg="gray-300" color="black">
                    received
                  </Badge>
                )}
              </div>
              {transfer && employee && (
                <div className="transfer-view__created-by">
                  <Text bodySmall dimmed>
                    Created by
                  </Text>
                  <Text body>{employee}</Text>
                </div>
              )}
              {transfer && !!sentDate && (
                <div className="transfer-view__sent">
                  <Text bodySmall dimmed>
                    Sent
                  </Text>
                  <Text body>{sentDate}</Text>
                </div>
              )}
            </CardSection>
          </Card>
        </div>
        <div className="transfer-view__details">
          <Card>
            <CardSection>
              <div className="transfer-view__shops">
                <div className="transfer-view__sending-shop">
                  <div className="cr-mb-1">
                    <Text bodySmall>From / Sending shop</Text>
                  </div>
                  <Autocomplete
                    name="fromShop"
                    className="transfer-view__autocomplete"
                    placeholder="Choose a shop"
                    options={shops}
                    value={fromShop}
                    clearable
                    disabled={!canChangeShops}
                    onChange={value => {
                      this.handleShopChange({
                        value: value || '',
                        selected: 'from',
                      });
                    }}
                  />
                </div>
                <div className="transfer-view__shops-spacer">
                  <IconArrowRight size="0.875rem" baseColor="#2875c6" />
                </div>
                <div className="transfer-view__receiving-shop">
                  <div className="cr-mb-1">
                    <Text bodySmall>To / Receiving shop</Text>
                  </div>
                  <Autocomplete
                    name="toShop"
                    className="transfer-view__autocomplete"
                    placeholder="Choose a shop"
                    options={shops}
                    value={toShop}
                    clearable
                    disabled={!canChangeShops}
                    onChange={value => {
                      this.handleShopChange({
                        value: value || '',
                        selected: 'to',
                      });
                    }}
                  />
                </div>
              </div>
            </CardSection>
            <Divider />
            <CardSection>
              <div className="transfer-view__need-by">
                <div className="transfer-view__need-by-label">
                  <Text bodySmall>Need by</Text>
                </div>
                <div className="transfer-view__need-by-date" ref={this.dateRef} />
              </div>
            </CardSection>
          </Card>
        </div>
      </div>
    );
  }
}

Transfer.propTypes = {
  transfer: PropTypes.shape({
    transferId: PropTypes.string,
    status: PropTypes.string,
    createdBy: PropTypes.string,
    sentDate: PropTypes.shape({
      date: PropTypes.string,
    }),
    dateTimeFormat: PropTypes.string,
    fromShopId: PropTypes.string,
    toShopId: PropTypes.string,
  }),
  shops: PropTypes.arrayOf(
    PropTypes.shape({
      shopId: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  canChangeShops: PropTypes.bool.isRequired,
  currentShopId: PropTypes.string.isRequired,
  inputs: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
    needBy: PropTypes.string,
  }).isRequired,
};

Transfer.defaultProps = {
  transfer: null,
};

const RenderedComponent = createReactRenderer({
  component: Transfer,
  selector: '[data-react-root="Transfer"]',
});

history.onChangeState(
  { name: 'transfer.views.transfer', id: undefined },
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
